import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  markAllNotificationsRead,
  markNotificationRead,
  useNotifications,
  type NotificationItem,
} from "@/lib/notificationStore";
import { cn } from "@/lib/cn";
import { displayTimeZone } from "@/lib/timeZone";
import { useOptionalAuth } from "@/auth/AuthContext";
import { toBcp47 } from "@/i18n";
import type { AiLimitOut } from "@/lib/aiApi";
import { formatLimitAmount, useAiLimits } from "@/lib/aiLimits";
import { BellIcon } from "@/components/ui/icons";

/**
 * Topbar bell showing the live SSE `ticket_new_in_queue` notifications:
 * an unread-count pill and a dropdown list. Clicking an item opens the
 * ticket and marks it read. Session-only (no persistence — see the store).
 *
 * Admins additionally see every exhausted AI budget (queue token budget,
 * provider cost budget) on top. Those are state, not events: they come from
 * `GET /api/v1/admin/ai/limits`, stay until the budget frees up, and turn
 * the pill amber.
 */
export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const locale = toBcp47(i18n.language);
  const navigate = useNavigate();
  const { items, unreadCount } = useNotifications();
  const auth = useOptionalAuth();
  const limitsQ = useAiLimits(auth?.user?.is_admin === true);
  const limits = (limitsQ.data?.items ?? []).filter((l) => l.exhausted);
  const [open, setOpen] = useState(false);
  const pillCount = unreadCount + limits.length;

  const openLimit = (limit: AiLimitOut) => {
    setOpen(false);
    void navigate({
      to: limit.kind === "queue_tokens_day" ? "/admin/ai/queues" : "/admin/ai/providers",
    });
  };

  const limitTitle = (limit: AiLimitOut) =>
    limit.kind === "queue_tokens_day"
      ? t("notifications.aiLimitQueue", { name: limit.subject_name })
      : t("notifications.aiLimitProvider", {
          name: limit.subject_name,
          window: t(`notifications.aiLimitWindow.${limit.window}`),
        });

  const resetTime = (limit: AiLimitOut) =>
    new Intl.DateTimeFormat(locale, {
      dateStyle: limit.window === "day" ? undefined : "short",
      timeStyle: "short",
      timeZone: displayTimeZone(),
    }).format(new Date(limit.resets_at));

  const openItem = (item: NotificationItem) => {
    markNotificationRead(item.id);
    setOpen(false);
    void navigate({
      to: "/agent/tickets/$ticketId",
      params: { ticketId: String(item.ticketId) },
    });
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="notification-bell"
        aria-label={t("notifications.title")}
        onClick={() => {
          setOpen((o) => {
            const next = !o;
            if (next) markAllNotificationsRead();
            return next;
          });
        }}
        className="relative flex h-8 w-8 items-center justify-center rounded-lg text-ink/70 transition-colors duration-100 hover:bg-surface-subtle hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
      >
        <BellIcon className="text-[17px]" />
        {pillCount > 0 && (
          <span
            data-testid="notification-unread-count"
            className={cn(
              "absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold tabular-nums",
              limits.length > 0 ? "bg-escalation text-white" : "bg-accent text-accent-ink",
            )}
          >
            {pillCount > 9 ? "9+" : pillCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-30 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            data-testid="notification-panel"
            className="absolute right-0 z-40 mt-1.5 w-80 max-w-[90vw] max-sm:fixed max-sm:inset-x-2 max-sm:top-12 max-sm:w-auto max-sm:max-w-none overflow-hidden rounded-lg border border-hairline bg-surface shadow-xl"
          >
            <div className="border-b border-hairline px-3 py-2 text-[12px] font-semibold text-ink">
              {t("notifications.title")}
            </div>
            {limits.length > 0 && (
              <ul
                className="list-none border-b border-hairline bg-escalation/10"
                data-testid="notification-ai-limits"
              >
                {limits.map((limit) => (
                  <li key={`${limit.kind}-${limit.subject_id}-${limit.window}`}>
                    <button
                      type="button"
                      data-testid={`notification-ai-limit-${limit.kind}-${limit.subject_id}`}
                      onClick={() => openLimit(limit)}
                      className="flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors duration-100 hover:bg-escalation/15"
                    >
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-escalation">
                        {t("notifications.aiLimits")}
                      </span>
                      <span className="truncate text-[12.5px] text-ink">{limitTitle(limit)}</span>
                      <span className="text-[11px] tabular-nums text-muted">
                        {t("notifications.aiLimitDetail", {
                          amount:
                            limit.kind === "queue_tokens_day"
                              ? `${formatLimitAmount(limit, locale)} ${t("notifications.aiLimitTokensUnit")}`
                              : formatLimitAmount(limit, locale),
                          time: resetTime(limit),
                        })}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {items.length === 0 && limits.length === 0 ? (
              <p className="px-3 py-6 text-center text-[12.5px] text-muted">
                {t("notifications.empty")}
              </p>
            ) : items.length === 0 ? null : (
              <ul className="max-h-80 list-none overflow-y-auto">
                {items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      data-testid={`notification-item-${item.ticketId}`}
                      onClick={() => openItem(item)}
                      className={cn(
                        "flex w-full flex-col gap-0.5 border-b border-hairline px-3 py-2 text-left transition-colors duration-100 last:border-b-0 hover:bg-surface-subtle",
                        !item.read && "bg-accent-dim/40",
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-[11px] tabular-nums text-accent">
                          {item.tn}
                        </span>
                        <span className="truncate text-[11px] text-muted">{item.queueName}</span>
                      </span>
                      <span className="truncate text-[12.5px] text-ink">
                        {item.title || t("notifications.noSubject")}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Transient toast overlay for freshly-arrived notifications. Mounted once
 * near the app shell; watches the store's newest item and shows a briefly
 * auto-dismissing card. Skips the initial snapshot so a reload doesn't
 * replay old items as toasts.
 */
export function NotificationToaster() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { items } = useNotifications();
  const [toasts, setToasts] = useState<NotificationItem[]>([]);
  // `undefined` = first effect run not yet seen; `null` = initialised on an
  // empty store; a string = id of the last head item we've reacted to.
  const lastSeenId = useRef<string | null | undefined>(undefined);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const top = items[0];
    if (lastSeenId.current === undefined) {
      // First effect run — adopt whatever's already at the head (if any)
      // without toasting it, so a reload doesn't replay old items. Recording
      // the baseline even when the store starts empty (null) ensures the
      // *next* arriving item is genuinely new and gets toasted, rather than
      // being silently adopted as the new baseline.
      lastSeenId.current = top?.id ?? null;
      return;
    }
    if (!top) return;
    if (top.id === lastSeenId.current) return;
    lastSeenId.current = top.id;
    const { id } = top;
    setToasts((cur) => [top, ...cur].slice(0, 3));
    // Each toast owns an independent auto-dismiss timer. This must NOT be
    // torn down when the next notification arrives (a later effect run),
    // otherwise every toast but the most recent would linger forever.
    const timer = setTimeout(() => {
      setToasts((cur) => cur.filter((tt) => tt.id !== id));
      timers.current = timers.current.filter((handle) => handle !== timer);
    }, 5000);
    timers.current.push(timer);
  }, [items]);

  // Clear any still-pending auto-dismiss timers on unmount.
  useEffect(
    () => () => {
      for (const handle of timers.current) clearTimeout(handle);
    },
    [],
  );

  if (toasts.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      data-testid="notification-toaster"
    >
      {toasts.map((item) => (
        <button
          type="button"
          key={item.id}
          data-testid={`notification-toast-${item.ticketId}`}
          onClick={() => {
            markNotificationRead(item.id);
            setToasts((cur) => cur.filter((tt) => tt.id !== item.id));
            void navigate({
              to: "/agent/tickets/$ticketId",
              params: { ticketId: String(item.ticketId) },
            });
          }}
          className="pointer-events-auto w-72 max-w-[90vw] rounded-lg border border-hairline bg-surface p-3 text-left shadow-xl animate-route-in"
        >
          <div className="flex items-center gap-2">
            <BellIcon className="text-[14px] text-accent" />
            <span className="text-[11px] font-semibold uppercase tracking-wide text-accent">
              {t("notifications.newTicket")}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span className="font-mono text-[11px] tabular-nums text-accent">{item.tn}</span>
            <span className="truncate text-[11px] text-muted">{item.queueName}</span>
          </div>
          <p className="mt-0.5 truncate text-[12.5px] text-ink">
            {item.title || t("notifications.noSubject")}
          </p>
        </button>
      ))}
    </div>
  );
}
